import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * JP Button — primary interaction element per design system v1.2
 *
 * Pill silhouette (rounded-full) matching the JP interactive language
 * (theme toggle, scroll-hint pill, chips). Ink text on bright fills per
 * the highlighter-on-paper rule established by Badge.
 *
 * Color roles per v1.2 §12.2:
 * - Primary: Orange fill — CTA and momentum, with the jp CTA glow
 * - Secondary/Outline: Orange edge, transparent fill
 * - Destructive: Magenta (palette has no red)
 * - Focus ring: Cyan — digital clarity and interactions
 *
 * Both themes resolve via jp-* tokens (shared app appearance or an explicit
 * section theme) — no `dark:` variants.
 *
 * Sizing: minimum 48px touch target on default, 12px+ radius via pill.
 */

const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center gap-2',
    'font-jp-body font-bold text-base',
    'rounded-full',
    'transition-all duration-200 ease-out',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent-secondary',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-jp-bg-page',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-jp-accent-primary text-jp-ink',
          'shadow-[shadow:var(--jp-shadow-cta)]',
          'hover:brightness-110 active:brightness-95',
        ],
        secondary: [
          'bg-transparent text-jp-accent-cta-text',
          'border-2 border-jp-accent-primary',
          'hover:bg-jp-accent-primary/10 active:bg-jp-accent-primary/20',
        ],
        // Outline variant (legacy alias)
        outline: [
          'bg-transparent text-jp-accent-cta-text',
          'border-2 border-jp-accent-primary',
          'hover:bg-jp-accent-primary/10 active:bg-jp-accent-primary/20',
        ],
        tertiary: [
          'bg-transparent text-jp-fg-primary',
          'underline-offset-4 hover:underline',
        ],
        icon: [
          'bg-transparent text-jp-fg-primary',
          'hover:bg-jp-bg-utility active:bg-jp-line-edge',
        ],
        // Ghost variant for subtle actions
        ghost: [
          'bg-transparent text-jp-fg-secondary',
          'hover:bg-jp-bg-utility hover:text-jp-fg-primary',
        ],
        // Destructive — magenta per v1.2 (no red in palette), ink text per Badge
        destructive: [
          'bg-jp-accent-magenta text-jp-ink',
          'hover:brightness-110 active:brightness-95',
        ],
      },
      size: {
        default: 'min-h-12 px-7 py-3.5',
        // Medium size (legacy alias)
        md: 'min-h-12 px-7 py-3.5',
        sm: 'min-h-10 px-4 py-2 text-sm',
        lg: 'min-h-14 px-8 py-4 text-lg',
        icon: 'min-h-11 min-w-11 p-2.5',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element (for Links) */
  asChild?: boolean;
  /** Loading state - shows spinner and disables button */
  isLoading?: boolean;
  /** Icon to display before children */
  leftIcon?: React.ReactNode;
  /** Icon to display after children */
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      isLoading,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // When asChild is true, pass styles to child element directly
    // The child is responsible for its own content
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <svg
            className="h-5 w-5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
